import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartPie,
    faBuilding,
    faStore,
    faList,
    faUsers,
    faTv,
    faExternalLinkAlt,
    faChevronLeft,
    faChevronRight,
    faCreditCard,
    faPuzzlePiece,
    faBolt,
    faShieldAlt,
    faDatabase,
    faGem,
    faRocket,
    faBriefcase,
    faChevronDown,
    faFolder,
    faFolderOpen,
    faCube
} from '@fortawesome/free-solid-svg-icons';
import * as SolidIcons from '@fortawesome/free-solid-svg-icons';
import DataManagerLogo from '../resources/DataManagerLogo';

const iconMap = {
    faChartPie, faBuilding, faStore, faList, faUsers, faTv, faExternalLinkAlt,
    faCreditCard, faPuzzlePiece, faBolt, faShieldAlt, faDatabase, faGem, faRocket, faBriefcase
};

/**
 * SidebarItem — Recursive component to render menu items and their children.
 */
const SidebarItem = ({ item, level = 0, activeTab, onTabChange, isCollapsed }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasChildren = (item.items && item.items.length > 0) || (item.menus && item.menus.length > 0);

    const isActive = (activeTab === item.id);
    const children = [...(item.menus || []), ...(item.items || [])];

    const toggleOpen = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    return (
        <div className="sa-nav-node">
            <div
                className={`sa-nav-item ${isActive ? 'active' : ''} ${hasChildren ? 'has-children' : ''}`}
                style={{
                    paddingLeft: isCollapsed ? '1.1rem' : `${1.1 + level * 0.8}rem`,
                    cursor: 'pointer'
                }}
                onClick={(e) => hasChildren ? toggleOpen(e) : onTabChange(item.id)}
            >
                <span className="sa-nav-icon">
                    <FontAwesomeIcon icon={item.icon || (hasChildren ? (isOpen ? faFolderOpen : faFolder) : faCube)} />
                </span>
                {!isCollapsed && (
                    <>
                        <motion.span
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            style={{ flex: 1 }}
                        >
                            {item.label}
                        </motion.span>
                        {hasChildren && (
                            <motion.span
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                style={{ fontSize: '0.7rem', opacity: 0.5 }}
                            >
                                <FontAwesomeIcon icon={faChevronDown} />
                            </motion.span>
                        )}
                    </>
                )}
            </div>

            <AnimatePresence>
                {hasChildren && isOpen && !isCollapsed && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                        style={{ overflow: 'hidden', borderLeft: '1px solid rgba(168, 85, 247, 0.1)', marginLeft: '1.8rem' }}
                    >
                        {children.map((child, idx) => (
                            <SidebarItem
                                key={child.id || `sub-${level}-${idx}`}
                                item={child}
                                level={level + 1}
                                activeTab={activeTab}
                                onTabChange={onTabChange}
                                isCollapsed={isCollapsed}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

/**
 * AdminSidebar - Premium collapsible navigation with grouped categories and hierarchical dynamic menu
 */
const AdminSidebar = ({ activeTab, onTabChange, isCollapsed, toggleCollapse, menuGroups: dynamicMenu = [] }) => {

    const user = JSON.parse(localStorage.getItem('dm_admin') || '{}');
    const userRole = user.AdminRole;
    
    const mapIcon = (iconName) => {
        if (!iconName) return null;
        if (typeof iconName !== 'string') return iconName;
        // Check local iconMap first (for baseMenu icons)
        if (iconMap[iconName]) return iconMap[iconName];
        return SolidIcons[iconName] || faCube;
    };

    // Recursive mapper to handle any depth in dynamic menu
    const mapDynamicItem = (item) => ({
        id: item.id || item.ModuleKey || item.GroupId,
        label: item.label || item.GroupLabel || item.ModuleLabel,
        icon: mapIcon(item.icon || item.GroupIcon || item.ModuleIcon),
        type: item.type,
        items: (item.items || []).map(mapDynamicItem),
        menus: (item.menus || []).map(mapDynamicItem),
        badge: item.badge
    });

    const menuGroups = [];

    // Static sections
    if (userRole === 'ADMIN' || userRole === 'SUPERADMIN') {
        menuGroups.push({
            title: 'Resumen',
            items: [
                { id: 'dashboard', label: 'Dashboard', icon: faChartPie },
            ]
        });
    }

    if (userRole === 'ADMIN') {
        menuGroups.push({
            title: 'Configuración Base',
            items: [
                { id: 'empresa', label: 'Mi Empresa', icon: faBuilding },
                { id: 'sucursales', label: 'Mis Sucursales', icon: faStore },
                { id: 'users', label: 'Mi Equipo (Staff)', icon: faUsers },
                { id: 'subscription', label: 'Mi Suscripción', icon: faCreditCard }
            ]
        });
        menuGroups.push({
            title: 'Constructores (Builders)',
            items: [
                { id: 'connections', label: 'Conexiones', icon: faDatabase },
                { id: 'querybuilder', label: 'Query Builder', icon: faRocket },
                { id: 'menubuilder', label: 'Menu Builder', icon: faPuzzlePiece }
            ]
        });
    }

    // Process Dynamic Menu (Ported grouping logic)
    const groupMap = {};
    dynamicMenu.forEach(g => {
        const gId = String(g.GroupId || g.id || (g.items && g.items[0]?.GroupId) || (g.items && g.items[0]?.ModuleGroupId) || '');
        const pId = g.ParentGroupId || (g.items && g.items[0]?.ParentGroupId) || null;

        if (gId && gId !== 'undefined' && gId !== '') {
            groupMap[gId] = {
                ...g,
                id: gId,
                ParentGroupId: pId,
                label: g.GroupLabel || g.label || g.title,
                menus: g.menus || [],
                items: g.items || []
            };
        } else {
            menuGroups.push({
                title: g.GroupLabel || g.label || g.title,
                items: (g.items || []).map(mapDynamicItem),
                menus: (g.menus || []).map(mapDynamicItem)
            });
        }
    });

    const dynamicRootGroups = [];
    Object.values(groupMap).forEach(g => {
        const pId = g.ParentGroupId ? String(g.ParentGroupId) : null;
        if (pId && groupMap[pId]) {
            groupMap[pId].menus.push(g);
        } else {
            dynamicRootGroups.push(g);
        }
    });

    dynamicRootGroups.forEach(group => {
        const hasContent = (group.items && group.items.length > 0) || (group.menus && group.menus.length > 0);
        if (hasContent) {
            menuGroups.push({
                title: group.label,
                items: (group.items || []).map(mapDynamicItem),
                menus: (group.menus || []).map(mapDynamicItem)
            });
        }
    });

    return (
        <motion.div
            className="admin-sidebar"
            initial={false}
            animate={{ width: isCollapsed ? 80 : 280 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
        >
            <div className={`sidebar-header d-flex flex-column align-items-center justify-content-center position-relative ${isCollapsed ? 'py-4' : 'py-5'}`}>
                <motion.div
                    initial={false}
                    animate={{ scale: isCollapsed ? 0.4 : 1, y: isCollapsed ? 0 : 0 }}
                    className="d-flex flex-column align-items-center"
                >
                    <DataManagerLogo size={isCollapsed ? 40 : 50} className="mb-2" />
                    {!isCollapsed && (
                        <h5 className=" mb-0" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                            DataManager
                        </h5>
                    )}
                </motion.div>

                <button
                    className="btn-collapse"
                    onClick={toggleCollapse}
                    title={isCollapsed ? 'Expandir' : 'Colapsar'}
                >
                    <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
                </button>
            </div>

            <div className="sidebar-nav custom-scrollbar" style={{ overflowY: 'auto', flex: 1 }}>
                {menuGroups.map((group, gIdx) => (
                    <div key={gIdx} className="mb-4">
                        {!isCollapsed && (
                            <div className="sidebar-group-label">
                                {group.title}
                            </div>
                        )}
                        <div className="d-flex flex-column gap-1">
                            {[...(group.menus || []), ...(group.items || [])].map((item, iIdx) => (
                                <SidebarItem
                                    key={item.id || `g${gIdx}-i${iIdx}`}
                                    item={item}
                                    activeTab={activeTab}
                                    onTabChange={onTabChange}
                                    isCollapsed={isCollapsed}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="sidebar-footer">
                {!isCollapsed && (
                    <div className="d-flex flex-column">
                        <small className="tiny text-uppercase font-weight-bold opacity-30">OctoCore Engine</small>
                        <small className="tiny font-weight-bold text-primary">v2.1 Premium</small>
                    </div>
                )}
                {isCollapsed && <small className="tiny font-weight-bold text-primary">v2.1</small>}
            </div>
        </motion.div>
    );
};

export default AdminSidebar;
